import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, List, ListOrdered } from 'lucide-react';

const BULLET_STYLES = [
    { key: 'disc', symbol: '●', label: 'Solid Circle' },
    { key: 'circle', symbol: '○', label: 'Open Circle' },
    { key: 'square', symbol: '■', label: 'Solid Square' },
    { key: 'dash', symbol: '—', label: 'Dash' },
    { key: 'arrow', symbol: '➜', label: 'Arrow' },
    { key: 'diamond', symbol: '◆', label: 'Diamond' },
];

const NUMBERING_STYLES = [
    { key: '1', items: ['1.', '2.', '3.'], label: '1. 2. 3.' },
    { key: 'A', items: ['A.', 'B.', 'C.'], label: 'A. B. C.' },
    { key: 'a', items: ['a.', 'b.', 'c.'], label: 'a. b. c.' },
    { key: 'I', items: ['I.', 'II.', 'III.'], label: 'I. II. III.' },
    { key: 'i', items: ['i.', 'ii.', 'iii.'], label: 'i. ii. iii.' },
];

export const BulletListDropdown = ({ editor, handleFormatAction }) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) setIsOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const isListActive = editor?.isActive('bulletList');

    const handleSelect = (styleKey) => {
        if (styleKey === 'none') {
            if (isListActive) handleFormatAction('Bullet List');
        } else {
            handleFormatAction(`BulletList:${styleKey}`);
        }
        setIsOpen(false);
    };

    return (
        <div className="list-style-dropdown-container" ref={dropdownRef}>
            <button
                className={`toolbar-btn split-btn-left ${isListActive ? 'active' : ''}`}
                onMouseDown={(e) => { e.preventDefault(); handleFormatAction('Bullet List'); }}
                title="Bullet List"
            >
                <List size={16} />
                <span className="toolbar-tooltip">Bullet List</span>
            </button>
            <button
                className="toolbar-btn split-btn-right"
                onMouseDown={(e) => { e.preventDefault(); setIsOpen(!isOpen); }}
                title="Bullet Library"
            >
                <ChevronDown size={12} />
            </button>
            {isOpen && (
                <div className="library-dropdown-menu">
                    <div className="library-dropdown-title">Bullet Library</div>
                    <div className="library-grid">
                        <div
                            className={`library-grid-item ${!isListActive ? 'active' : ''}`}
                            onMouseDown={(e) => { e.preventDefault(); handleSelect('none'); }}
                            title="None"
                        >
                            <span className="none-text">None</span>
                        </div>
                        {BULLET_STYLES.map((item) => (
                            <div
                                key={item.key}
                                className="library-grid-item"
                                onMouseDown={(e) => { e.preventDefault(); handleSelect(item.key); }}
                                title={item.label}
                            >
                                <div className="numbering-preview-lines">
                                    {[0, 1, 2].map((i) => (
                                        <div key={i} className="numbering-line">
                                            <span style={{ fontSize: '0.9rem', lineHeight: '0.7rem' }}>{item.symbol}</span>
                                            <div className="line-indicator"></div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export const OrderedListDropdown = ({ editor, handleFormatAction }) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) setIsOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const isListActive = editor?.isActive('orderedList');

    const handleSelect = (styleKey) => {
        if (styleKey === 'none') {
            if (isListActive) handleFormatAction('Numbered List');
        } else {
            handleFormatAction(`OrderedList:${styleKey}`);
        }
        setIsOpen(false);
    };

    return (
        <div className="list-style-dropdown-container" ref={dropdownRef}>
            <button
                className={`toolbar-btn split-btn-left ${isListActive ? 'active' : ''}`}
                onMouseDown={(e) => { e.preventDefault(); handleFormatAction('Numbered List'); }}
                title="Numbered List"
            >
                <ListOrdered size={16} />
                <span className="toolbar-tooltip">Numbered List</span>
            </button>
            <button
                className="toolbar-btn split-btn-right"
                onMouseDown={(e) => { e.preventDefault(); setIsOpen(!isOpen); }}
                title="Numbering Library"
            >
                <ChevronDown size={12} />
            </button>
            {isOpen && (
                <div className="library-dropdown-menu">
                    <div className="library-dropdown-title">Numbering Library</div>
                    <div className="library-grid">
                        <div
                            className={`library-grid-item ${!isListActive ? 'active' : ''}`}
                            onMouseDown={(e) => { e.preventDefault(); handleSelect('none'); }}
                            title="None"
                        >
                            <span className="none-text">None</span>
                        </div>
                        {NUMBERING_STYLES.map((item) => (
                            <div
                                key={item.key}
                                className="library-grid-item"
                                onMouseDown={(e) => { e.preventDefault(); handleSelect(item.key); }}
                                title={item.label}
                            >
                                <div className="numbering-preview-lines">
                                    {item.items.map((prefix, idx) => (
                                        <div key={idx} className="numbering-line">
                                            <span style={{ fontSize: '0.75rem', minWidth: '14px' }}>{prefix}</span>
                                            <div className="line-indicator"></div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};
